/**
 * Workspace groups (ADR-041): one org workspace's groups — Workspace Controls →
 * Groups.
 *
 * URL: /workspaces/:holderId/groups and /workspaces/:holderId/groups/:groupId.
 *
 * A group is a named set of the workspace's own members ("Board", "HR"). It
 * grants nothing by itself: put on a named label, its members can read the
 * label, each up to their workspace role. Every seat sees every group's name;
 * only owners, admins and the group's own members see who is in it.
 *
 * Adding someone to a group that reads labels lets them read those labels, so
 * the add is sent first without proof and, when the server asks for it
 * (`STEP_UP_REQUIRED`), sent again with it. For a label the adder doesn't
 * manage, the new member waits for a manager of that label to approve them.
 *
 * The server decides every answer; the screen only avoids offering what would
 * be refused.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Users } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import {
  VAULT_WORKSPACE_GROUP_DESCRIPTION_MAX,
  VAULT_WORKSPACE_GROUP_NAME_MAX,
  type VaultHolderMemberDto,
  type VaultWorkspaceGroupMemberDto,
  type VaultWorkspaceGroupSummaryDto,
} from '@/lib/api';
import { StepUpDialog } from '../components/StepUpDialog';
import { namedLabelErrorCode, useMyWorkspaceSeat } from '../hooks/useVaultNamedLabels';
import {
  addVaultWorkspaceGroupMember,
  addedToGroupMessage,
  describeWorkspaceGroupError,
  plainStepUpError,
  useCanCreateVaultWorkspaceGroup,
  useCreateVaultWorkspaceGroup,
  useDeleteVaultWorkspaceGroup,
  useRemoveVaultWorkspaceGroupMember,
  useSetVaultWorkspaceGroupManager,
  useUpdateVaultWorkspaceGroup,
  useVaultWorkspaceGroupMembers,
  useVaultWorkspaceGroups,
  useVaultWorkspaceGroupsInvalidator,
} from '../hooks/useVaultWorkspaceGroups';
import { useWorkspaceMembers } from '../hooks/useWorkspaceTeam';
import {
  isPrivilegedTier,
  namedLabelsPath,
  personLabel,
  TIER_NAMES,
  workspaceGroupDetailPath,
  workspaceGroupsPath,
} from './named-labels/shared';

export function WorkspaceGroupsPage() {
  const params = useParams<{ holderId: string; groupId?: string }>();
  const holderId = params.holderId ?? '';
  const groupId = params.groupId ?? null;
  const seat = useMyWorkspaceSeat(holderId);
  const groups = useVaultWorkspaceGroups(holderId || null);
  const canCreate = useCanCreateVaultWorkspaceGroup(holderId || null);
  const members = useWorkspaceMembers(holderId || null);
  const privileged = isPrivilegedTier(seat.role);
  const group = groupId ? groups.data?.find((g) => g.groupId === groupId) : undefined;

  return (
    <div>
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-4xl px-4 py-4 sm:px-6 lg:px-8">
          <p className="text-sm">
            <Link to="/workspaces" className="text-muted-foreground hover:underline">
              Workspace Controls
            </Link>
            {' / '}
            <Link to={namedLabelsPath(holderId)} className="text-muted-foreground hover:underline">
              Named labels
            </Link>
            {groupId && (
              <>
                {' / '}
                <Link
                  to={workspaceGroupsPath(holderId)}
                  className="text-muted-foreground hover:underline"
                >
                  Groups
                </Link>
              </>
            )}
          </p>
          <h1 className="text-2xl font-bold text-foreground">{group ? group.name : 'Groups'}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            A group is a named set of people in this workspace, like “Board” or “HR”. On its own it
            gives no access; put it on a named label and everyone in it can read the label’s
            documents, each up to their workspace role.
          </p>
        </div>
      </div>
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        {seat.isLoading || groups.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : groups.isError ? (
          <p className="text-sm text-destructive">
            {describeWorkspaceGroupError(groups.error, 'Groups could not be loaded.')}
          </p>
        ) : groupId ? (
          group ? (
            <GroupDetail
              holderId={holderId}
              group={group}
              privileged={privileged}
              myPrincipalId={seat.principalId}
              members={members.data ?? []}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              This group isn’t available — it may have been deleted.
            </p>
          )
        ) : (
          <>
            <GroupsListCard holderId={holderId} groups={groups.data ?? []} />
            {canCreate.data === true && <CreateGroupCard holderId={holderId} />}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The groups
// ---------------------------------------------------------------------------

function GroupsListCard({
  holderId,
  groups,
}: {
  holderId: string;
  groups: readonly VaultWorkspaceGroupSummaryDto[];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-4 w-4" aria-hidden="true" />
          Groups
        </CardTitle>
        <CardDescription>
          Every group in this workspace. You see who’s in a group if you’re in it, or an owner or
          admin.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">No groups yet.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {groups.map((g) => (
              <li key={g.groupId} className="px-3 py-2">
                <Link
                  to={workspaceGroupDetailPath(holderId, g.groupId)}
                  className="flex flex-wrap items-center justify-between gap-2 hover:underline"
                >
                  <span className="font-medium text-foreground">{g.name}</span>
                  <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {g.memberCount !== null && (
                      <span>
                        {g.memberCount} {g.memberCount === 1 ? 'person' : 'people'}
                      </span>
                    )}
                    {g.labelsRead !== null && g.labelsRead > 0 && (
                      <span>
                        Reads {g.labelsRead} {g.labelsRead === 1 ? 'label' : 'labels'}
                      </span>
                    )}
                    {g.myMembership?.isManager ? (
                      <Badge variant="outline">You manage this</Badge>
                    ) : g.myMembership ? (
                      <Badge variant="outline">You’re in this</Badge>
                    ) : null}
                  </span>
                </Link>
                {g.description && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{g.description}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function CreateGroupCard({ holderId }: { holderId: string }) {
  const create = useCreateVaultWorkspaceGroup(holderId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">New group</CardTitle>
        <CardDescription>
          You’ll manage it. You can add people once it’s made, then put it on a label.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate(
              { name: name.trim(), description: description.trim() || null },
              {
                onSuccess: (group) => {
                  toast({ title: 'Group created', description: group.name });
                  setName('');
                  setDescription('');
                },
                onError: (err) =>
                  toast({
                    title: 'Could not create the group',
                    description: describeWorkspaceGroupError(err),
                    variant: 'destructive',
                  }),
              },
            );
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="workspace-group-name">Name</Label>
            <Input
              id="workspace-group-name"
              value={name}
              maxLength={VAULT_WORKSPACE_GROUP_NAME_MAX}
              placeholder="Board"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="workspace-group-description">Description (optional)</Label>
            <Textarea
              id="workspace-group-description"
              value={description}
              maxLength={VAULT_WORKSPACE_GROUP_DESCRIPTION_MAX}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <Button type="submit" size="sm" disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create group'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// One group
// ---------------------------------------------------------------------------

function GroupDetail({
  holderId,
  group,
  privileged,
  myPrincipalId,
  members,
}: {
  holderId: string;
  group: VaultWorkspaceGroupSummaryDto;
  privileged: boolean;
  myPrincipalId: string | null;
  members: readonly VaultHolderMemberDto[];
}) {
  const isManager = group.myMembership?.isManager === true;
  const isIn = group.myMembership !== null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" aria-hidden="true" />
            {group.name}
          </CardTitle>
          {group.description && <CardDescription>{group.description}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          {group.labelsRead !== null && (
            <p>
              {group.labelsRead === 0
                ? 'This group doesn’t read any labels.'
                : `This group reads ${group.labelsRead} ${group.labelsRead === 1 ? 'label' : 'labels'}. Someone you add can read a label straight away only if you manage that label; otherwise a manager of the label approves them first.`}
            </p>
          )}
          {isIn && (
            <p className="text-foreground">
              {isManager ? 'You manage this group.' : 'You’re in this group.'}
            </p>
          )}
          {!isIn && !privileged && <p>Only the people in this group can see who’s in it.</p>}
        </CardContent>
      </Card>

      {(isIn || privileged) && (
        <MembersCard
          holderId={holderId}
          group={group}
          isManager={isManager}
          privileged={privileged}
          myPrincipalId={myPrincipalId}
          members={members}
        />
      )}

      {/* Only owners and admins rename or describe a group (a group's managers
          look after who is in it). */}
      {privileged && <EditGroupCard holderId={holderId} group={group} />}

      {privileged && <DeleteGroupCard holderId={holderId} group={group} />}
    </div>
  );
}

function MembersCard({
  holderId,
  group,
  isManager,
  privileged,
  myPrincipalId,
  members,
}: {
  holderId: string;
  group: VaultWorkspaceGroupSummaryDto;
  isManager: boolean;
  privileged: boolean;
  myPrincipalId: string | null;
  members: readonly VaultHolderMemberDto[];
}) {
  const groupMembers = useVaultWorkspaceGroupMembers(holderId, group.groupId);
  const setManager = useSetVaultWorkspaceGroupManager(holderId);
  const remove = useRemoveVaultWorkspaceGroupMember(holderId);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const canGovern = isManager || privileged;

  const onError = (title: string) => (err: unknown) =>
    toast({ title, description: describeWorkspaceGroupError(err), variant: 'destructive' });

  const tierOf = (m: VaultWorkspaceGroupMemberDto) =>
    members.find((w) => w.principalId === m.principalId)?.role ?? null;

  // The server's rule, on the same live members it counts: a group keeps a
  // manager while it has members. So the only manager can neither stop
  // managing nor leave (or be removed) while anyone else is in it — the
  // screen says why instead of offering a click the server refuses.
  const all = groupMembers.data ?? [];
  const managerCount = all.filter((m) => m.isManager).length;
  const soleManager = (m: VaultWorkspaceGroupMemberDto) => m.isManager && managerCount <= 1;
  const keepsGroupManaged = (m: VaultWorkspaceGroupMemberDto) => soleManager(m) && all.length > 1;

  /** Removing someone (or leaving) takes effect at once: ask first, and say what it costs. */
  const confirmRemoval = (m: VaultWorkspaceGroupMemberDto, isMe: boolean): Promise<boolean> => {
    const readsLabels = (group.labelsRead ?? 0) > 0;
    const consequence = readsLabels
      ? isMe
        ? 'You’ll stop reading every label you read through this group, at once.'
        : 'They’ll stop reading every label they read through this group, at once. Adding them back starts over: each label’s manager clears them again.'
      : isMe
        ? 'A manager can add you back.'
        : 'You can add them back.';
    return confirm({
      title: isMe
        ? `Leave ${group.name}?`
        : `Remove ${personLabel(members, m.principalId, myPrincipalId)} from ${group.name}?`,
      description: consequence,
      confirmLabel: isMe ? 'Leave group' : 'Remove',
      destructive: true,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>People in this group</CardTitle>
        <CardDescription>Managers add and remove people. Anyone can leave a group.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {groupMembers.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : groupMembers.isError ? (
          <p className="text-sm text-destructive">
            {describeWorkspaceGroupError(groupMembers.error)}
          </p>
        ) : (groupMembers.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Nobody is in this group.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {(groupMembers.data ?? []).map((m) => {
              const isMe = m.principalId === myPrincipalId;
              const tier = tierOf(m);
              return (
                <li
                  key={m.memberId}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium text-foreground">
                      {personLabel(members, m.principalId, myPrincipalId)}
                      {m.isManager && <Badge variant="outline">Manager</Badge>}
                    </div>
                    {tier && <p className="text-xs text-muted-foreground">{TIER_NAMES[tier]}</p>}
                    {canGovern && keepsGroupManaged(m) && (
                      <p className="text-xs text-muted-foreground">
                        The only manager. Make someone else a manager first.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {canGovern && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={setManager.isPending || soleManager(m)}
                        onClick={() =>
                          setManager.mutate(
                            {
                              groupId: group.groupId,
                              principalId: m.principalId,
                              isManager: !m.isManager,
                            },
                            { onError: onError('Could not change who manages this group') },
                          )
                        }
                      >
                        {m.isManager ? 'Stop managing' : 'Make manager'}
                      </Button>
                    )}
                    {(canGovern || isMe) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={remove.isPending || keepsGroupManaged(m)}
                        onClick={async () => {
                          if (!(await confirmRemoval(m, isMe))) return;
                          remove.mutate(
                            { groupId: group.groupId, principalId: m.principalId },
                            {
                              onSuccess: () => {
                                toast({
                                  title: isMe ? 'You left the group' : 'Removed from the group',
                                });
                                if (isMe && !privileged) navigate(workspaceGroupsPath(holderId));
                              },
                              onError: onError(isMe ? 'Could not leave' : 'Could not remove'),
                            },
                          );
                        }}
                      >
                        {isMe ? 'Leave' : 'Remove'}
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* A manager adds. A group left with no manager (its last one left, or
            lost their seat) is restarted by an owner or admin adding someone —
            the server's rule, on the same live members this list shows. */}
        {(isManager ||
          (privileged &&
            groupMembers.isSuccess &&
            !groupMembers.data.some((m) => m.isManager))) && (
          <AddMemberForm
            holderId={holderId}
            group={group}
            members={members}
            inGroup={(groupMembers.data ?? []).map((m) => m.principalId)}
          />
        )}
      </CardContent>
    </Card>
  );
}

function AddMemberForm({
  holderId,
  group,
  members,
  inGroup,
}: {
  holderId: string;
  group: VaultWorkspaceGroupSummaryDto;
  members: readonly VaultHolderMemberDto[];
  inGroup: string[];
}) {
  const invalidate = useVaultWorkspaceGroupsInvalidator(holderId);
  const [principalId, setPrincipalId] = useState('');
  const [asManager, setAsManager] = useState(false);
  const [sending, setSending] = useState(false);
  const [needsProof, setNeedsProof] = useState(false);
  const candidates = members.filter(
    (m) => m.status === 'active' && !inGroup.includes(m.principalId),
  );
  const chosen = members.find((m) => m.principalId === principalId);
  const body = { principalId, ...(asManager ? { isManager: true } : {}) };

  const done = (added: { labelsWaiting: number }) => {
    toast({ title: addedToGroupMessage(added) });
    setPrincipalId('');
    setAsManager(false);
    void invalidate();
  };

  async function add() {
    setSending(true);
    try {
      done(await addVaultWorkspaceGroupMember(holderId, group.groupId, body, null));
    } catch (err) {
      if (namedLabelErrorCode(err) === 'STEP_UP_REQUIRED') setNeedsProof(true);
      else
        toast({
          title: 'Could not add them',
          description: describeWorkspaceGroupError(err),
          variant: 'destructive',
        });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-dashed border-border p-3">
      <Label htmlFor="workspace-group-add-member">Add someone</Label>
      <div className="flex flex-wrap items-center gap-3">
        <select
          id="workspace-group-add-member"
          value={principalId}
          onChange={(e) => setPrincipalId(e.target.value)}
          className="min-w-[14rem] rounded border border-border bg-card px-2 py-1.5 text-sm"
        >
          <option value="">Choose a person…</option>
          {candidates.map((m) => (
            <option key={m.principalId} value={m.principalId}>
              {personLabel(members, m.principalId)} ({TIER_NAMES[m.role]})
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={asManager} onCheckedChange={setAsManager} />
          Can manage the group
        </label>
        <Button size="sm" disabled={!principalId || sending} onClick={() => void add()}>
          {sending ? 'Adding…' : 'Add'}
        </Button>
      </div>
      <StepUpDialog
        open={needsProof}
        onOpenChange={setNeedsProof}
        title={`Add ${chosen ? personLabel(members, chosen.principalId) : 'this person'} to ${group.name}?`}
        description="This group reads named labels, so they’ll be able to read those labels’ documents, each up to their workspace role — straight away for labels you manage, and once a manager approves for the rest. The people on each label are told. Confirm it’s you to continue."
        confirmLabel="Add to group"
        onConfirm={async (creds) => {
          try {
            done(await addVaultWorkspaceGroupMember(holderId, group.groupId, body, creds));
          } catch (err) {
            plainStepUpError(err);
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Name and description (owners and admins)
// ---------------------------------------------------------------------------

function EditGroupCard({
  holderId,
  group,
}: {
  holderId: string;
  group: VaultWorkspaceGroupSummaryDto;
}) {
  const update = useUpdateVaultWorkspaceGroup(holderId);
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? '');
  useEffect(() => {
    setName(group.name);
    setDescription(group.description ?? '');
  }, [group.name, group.description]);

  const dirty =
    name.trim() !== group.name || (description.trim() || null) !== (group.description ?? null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Group settings</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!dirty || !name.trim()) return;
            update.mutate(
              {
                groupId: group.groupId,
                name: name.trim(),
                description: description.trim() || null,
              },
              {
                onSuccess: () => toast({ title: 'Group saved' }),
                onError: (err) =>
                  toast({
                    title: 'Could not save the group',
                    description: describeWorkspaceGroupError(err),
                    variant: 'destructive',
                  }),
              },
            );
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="workspace-group-edit-name">Name</Label>
            <Input
              id="workspace-group-edit-name"
              value={name}
              maxLength={VAULT_WORKSPACE_GROUP_NAME_MAX}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="workspace-group-edit-description">Description</Label>
            <Textarea
              id="workspace-group-edit-description"
              value={description}
              maxLength={VAULT_WORKSPACE_GROUP_DESCRIPTION_MAX}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <Button type="submit" size="sm" disabled={!dirty || !name.trim() || update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Delete (owners and admins)
// ---------------------------------------------------------------------------

function DeleteGroupCard({
  holderId,
  group,
}: {
  holderId: string;
  group: VaultWorkspaceGroupSummaryDto;
}) {
  const remove = useDeleteVaultWorkspaceGroup(holderId);
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  // The server refuses to delete a group a label still reads
  // (VAULT_GROUP_READS_LABELS); owners and admins — the only ones shown this
  // card — always get the count.
  const readsLabels = (group.labelsRead ?? 0) > 0;

  return (
    <Card className="border-destructive/30">
      <CardHeader>
        <CardTitle className="text-base">Delete this group</CardTitle>
        <CardDescription>
          {readsLabels
            ? `This group still reads ${group.labelsRead} ${group.labelsRead === 1 ? 'label' : 'labels'}. Each label’s manager, or an owner, takes it off first; then it can be deleted.`
            : 'A group can only be deleted once no label reads it. Take it off its labels first.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          size="sm"
          variant="destructive"
          disabled={remove.isPending || readsLabels}
          onClick={() => setConfirming(true)}
        >
          Delete
        </Button>
      </CardContent>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {group.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The group and its list of people are removed. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                remove.mutate(group.groupId, {
                  onSuccess: () => {
                    toast({ title: 'Group deleted' });
                    navigate(workspaceGroupsPath(holderId));
                  },
                  onError: (err) =>
                    toast({
                      title: 'Could not delete the group',
                      description: describeWorkspaceGroupError(err),
                      variant: 'destructive',
                    }),
                })
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
